/**
 * Filters for sessions.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { Select } from '@/components/forms/Select';
import { SearchInput } from '@/components/forms/SearchInput';
import {
  SESSION_REMOTE_STATUS_CONFIG,
  SESSION_REMOTE_SEARCH_PLACEHOLDERS,
  SESSION_REMOTE_MEETING_RECURRENCE_FILTER_OPTIONS,
  DATE_RANGE_PERIOD_OPTIONS_FULL,
  type SessionRemoteFilters,
} from '@/components/sessions/session-configs';
import {
  getRemoteStatusKeysForTypes,
  resolveRemoteSessionFilterTypes,
} from './session-table-utils';
import type { SessionTab } from '@/types/remote/session.types';
import type { SessionStatus } from '@/types/inperson/session.types';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { useUsersSearch } from '@/hooks/useUsersSearch';
import {
  ManagedByMultiSelect,
  type ManagedByValue,
} from '@/components/common/ManagedByMultiSelect';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { getSchoolYears } from '@/services/school-years.service';

/** Props for {@link SessionFilters}. */
interface SessionFiltersProps {
  activeTab: SessionTab;
  filters: SessionRemoteFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleStatus: (status: string) => void;
  onSetDateRange: (range: SessionRemoteFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetSearch: (value: string | undefined) => void;
  onSetTutorFilter: (id: string | undefined) => void;
  onSetStudentFilter: (id: string | undefined) => void;
  onSetManagedByFilter: (value: ManagedByValue) => void;
  onSetProgramFilter: (id: string | undefined) => void;
  onSetSchoolYearFilter: (id: string | undefined) => void;
  onSetRecurrenceFilter: (v: string | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Period chips — picking a chip clears the custom date range. */
const PeriodFilter: React.FC<{
  filters: SessionRemoteFilters;
  onSetDateRange: (range: SessionRemoteFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetDateRange, onSetCustomDateRange }) => (
  <div className="filter-group">
    <span className="filter-group-label">Période</span>
    <SegmentedControl
      size="sm"
      value={filters.customDateFrom ? '' : (filters.dateRange ?? 'all')}
      onChange={(v) => {
        onSetDateRange(v as SessionRemoteFilters['dateRange']);
        onSetCustomDateRange(undefined, undefined);
      }}
      options={[...DATE_RANGE_PERIOD_OPTIONS_FULL]}
    />
  </div>
);

/** Custom date range — values are local calendar dates, not UTC. */
const DateRangeFilter: React.FC<{
  filters: SessionRemoteFilters;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetDateRange: (range: SessionRemoteFilters['dateRange']) => void;
}> = ({ filters, onSetCustomDateRange, onSetDateRange }) => {
  /** Local calendar date (`YYYY-MM-DD`) — avoid UTC shift from `toISOString`. */
  const toDateString = (d?: Date) => {
    if (!d) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fromStr = toDateString(filters.customDateFrom);
  const toStr = toDateString(filters.customDateTo);

  /** Parse as local midnight and drop the period chip (`all`). */
  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.customDateTo);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 0, 0, 0);
    onSetCustomDateRange(d, filters.customDateTo);
    onSetDateRange('all');
  };

  /** Parse as local end-of-day and drop the period chip (`all`). */
  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.customDateFrom, undefined);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 23, 59, 59);
    onSetCustomDateRange(filters.customDateFrom, d);
    onSetDateRange('all');
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date personnalisée</span>
      <div className="w-auto mt-0.5">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Status chips for the statuses allowed on the active tab. */
const StatusFilter: React.FC<{
  filters: SessionRemoteFilters;
  onToggleStatus: (status: string) => void;
  statusKeys: SessionStatus[];
}> = ({ filters, onToggleStatus, statusKeys }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut</span>
    <div className="filter-options">
      {statusKeys.map((key) => {
        const config = SESSION_REMOTE_STATUS_CONFIG[key];
        if (!config) return null;
        const Icon = config.icon;
        const isActive = filters.statuses.includes(key);

        const getBadgeClassName = () => {
          if (!isActive) return 'filter-badge-inactive';
          if (config.filterActiveVariant === 'solid') return 'filter-badge-active-solid';
          return 'filter-badge-active';
        };

        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
              getBadgeClassName(),
            )}
            style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
            onClick={() => onToggleStatus(key)}
          >
            {config.isInProgress ? (
              <span
                className="w-3 h-3 rounded-full shrink-0"
                style={{ background: isActive ? 'white' : 'rgb(var(--color-error))' }}
              />
            ) : (
              <Icon className="w-3.5 h-3.5" />
            )}
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Free-text search for remote session filters. */
const SearchFilter: React.FC<{
  filters: SessionRemoteFilters;
  onSetSearch: (value: string | undefined) => void;
  activeTab: SessionTab;
}> = ({ filters, onSetSearch, activeTab }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-56">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder={SESSION_REMOTE_SEARCH_PLACEHOLDERS[activeTab]}
        value={filters.search ?? ''}
        onSearch={(q) => onSetSearch(q.trim() || undefined)}
        onClear={() => onSetSearch(undefined)}
      />
    </div>
  </div>
);

/** Remote tutor autocomplete for session filters. */
const TutorFilter: React.FC<{
  filters: SessionRemoteFilters;
  onSetTutorFilter: (id: string | undefined) => void;
}> = ({ filters, onSetTutorFilter }) => {
  const {
    users: tutorUsers,
    isLoading,
    onSearch,
  } = useUsersSearch({
    role: 'tutor_remote',
    pageSize: 20,
  });
  const options = tutorUsers.map((u) => ({
    value: u.id,
    label: u.displayName || `${u.firstName} ${u.lastName}`,
  }));

  return (
    <div className="filter-group">
      <span className="filter-group-label">Tuteur</span>
      <div className="w-48">
        <Autocomplete
          inputSize="sm"
          placeholder="Rechercher un tuteur..."
          options={options}
          value={filters.tutorUserId ?? ''}
          onChange={(val) => onSetTutorFilter(val || undefined)}
          onSearch={onSearch}
          isLoading={isLoading}
          minSearchLength={2}
        />
      </div>
    </div>
  );
};

/** Remote student autocomplete for session filters. */
const StudentFilter: React.FC<{
  filters: SessionRemoteFilters;
  onSetStudentFilter: (id: string | undefined) => void;
}> = ({ filters, onSetStudentFilter }) => {
  const {
    users: studentUsers,
    isLoading,
    onSearch,
  } = useUsersSearch({
    role: 'student_remote',
    pageSize: 20,
  });
  const options = studentUsers.map((u) => ({
    value: u.id,
    label: u.displayName || `${u.firstName} ${u.lastName}`,
  }));

  return (
    <div className="filter-group">
      <span className="filter-group-label">Élève</span>
      <div className="w-48">
        <Autocomplete
          inputSize="sm"
          placeholder="Rechercher un élève..."
          options={options}
          value={filters.studentUserId ?? ''}
          onChange={(val) => onSetStudentFilter(val || undefined)}
          onSearch={onSearch}
          isLoading={isLoading}
          minSearchLength={2}
        />
      </div>
    </div>
  );
};

/** Managed-by multi-select for remote session filters. */
const ManagedByFilter: React.FC<{
  filters: SessionRemoteFilters;
  onSetManagedByFilter: (value: ManagedByValue) => void;
}> = ({ filters, onSetManagedByFilter }) => (
  <div className="filter-group">
    <span className="filter-group-label">Géré par</span>
    <div className="w-64">
      <ManagedByMultiSelect
        value={filters.managedByIds}
        onChange={onSetManagedByFilter}
        inputSize="sm"
      />
    </div>
  </div>
);

/** Program select for remote session filters. */
const ProgramFilter: React.FC<{
  filters: SessionRemoteFilters;
  onSetProgramFilter: (id: string | undefined) => void;
}> = ({ filters, onSetProgramFilter }) => {
  const { data: programsResponse } = useProgramsForMode();
  const selectOptions = React.useMemo(
    () =>
      (programsResponse?.data ?? []).map((p) => ({
        value: p.id,
        label: p.label ?? p.name ?? p.id,
      })),
    [programsResponse],
  );

  return (
    <div className="filter-group">
      <span className="filter-group-label">Programme</span>
      <div className="w-48">
        <Select
          key={filters.programId ?? '__empty__'}
          placeholder="Tous les programmes"
          options={selectOptions}
          value={filters.programId ?? ''}
          onChange={(val) => onSetProgramFilter(val || undefined)}
          size="sm"
        />
      </div>
    </div>
  );
};

/** School-year select for remote session filters. */
const SchoolYearFilter: React.FC<{
  filters: SessionRemoteFilters;
  onSetSchoolYearFilter: (id: string | undefined) => void;
}> = ({ filters, onSetSchoolYearFilter }) => {
  const { data: schoolYearsData } = useQuery({
    queryKey: ['school-years', 'filter-sessions-remote'],
    queryFn: () => getSchoolYears({ pageSize: 20 }),
  });
  const options = React.useMemo(
    () =>
      schoolYearsData?.data.map((sy) => ({
        value: sy.id,
        label: sy.label ?? sy.code ?? sy.id,
      })) ?? [],
    [schoolYearsData],
  );
  return (
    <div className="filter-group">
      <span className="filter-group-label">Année scolaire</span>
      <div className="w-48">
        <Select
          key={filters.schoolYearId ?? '__empty__'}
          placeholder="Toutes les années"
          size="sm"
          value={filters.schoolYearId ?? ''}
          onChange={(v) => onSetSchoolYearFilter(v || undefined)}
          options={options}
        />
      </div>
    </div>
  );
};

/** Recurrence filter for information-meeting and welcome-meeting tabs. */
const RecurrenceFilter: React.FC<{
  filters: SessionRemoteFilters;
  onSetRecurrenceFilter: (v: string | undefined) => void;
}> = ({ filters, onSetRecurrenceFilter }) => (
  <div className="filter-group">
    <span className="filter-group-label">Récurrence</span>
    <div className="w-48">
      <Select
        size="sm"
        value={filters.recurrenceType ?? ''}
        onChange={(v) => onSetRecurrenceFilter(v || undefined)}
        options={SESSION_REMOTE_MEETING_RECURRENCE_FILTER_OPTIONS}
      />
    </div>
  </div>
);

/** Filters for sessions. */
export const SessionFilters: React.FC<SessionFiltersProps> = ({
  activeTab,
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleStatus,
  onSetDateRange,
  onSetCustomDateRange,
  onSetSearch,
  onSetTutorFilter,
  onSetStudentFilter,
  onSetManagedByFilter,
  onSetProgramFilter,
  onSetSchoolYearFilter,
  onSetRecurrenceFilter,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  const effectiveTypes = React.useMemo(
    () => resolveRemoteSessionFilterTypes(activeTab, filters.types),
    [activeTab, filters.types],
  );

  const statusKeys = React.useMemo(
    () => getRemoteStatusKeysForTypes(effectiveTypes),
    [effectiveTypes],
  );

  const showTutoringSpecific =
    activeTab === 'tutoring' ||
    (activeTab === 'all' && effectiveTypes.includes('individual_tutoring'));

  return (
    <div className="filter-groups-container">
      {show('filter-search') && (
        <SearchFilter filters={filters} onSetSearch={onSetSearch} activeTab={activeTab} />
      )}

      {show('filter-period') && (
        <PeriodFilter
          filters={filters}
          onSetDateRange={onSetDateRange}
          onSetCustomDateRange={onSetCustomDateRange}
        />
      )}

      {show('filter-date-range') && (
        <DateRangeFilter
          filters={filters}
          onSetCustomDateRange={onSetCustomDateRange}
          onSetDateRange={onSetDateRange}
        />
      )}

      {show('filter-status') && (
        <StatusFilter filters={filters} onToggleStatus={onToggleStatus} statusKeys={statusKeys} />
      )}

      {showTutoringSpecific && show('filter-program') && (
        <ProgramFilter filters={filters} onSetProgramFilter={onSetProgramFilter} />
      )}

      {showTutoringSpecific && show('filter-tutor') && (
        <TutorFilter filters={filters} onSetTutorFilter={onSetTutorFilter} />
      )}

      {showTutoringSpecific && show('filter-student') && (
        <StudentFilter filters={filters} onSetStudentFilter={onSetStudentFilter} />
      )}

      {showTutoringSpecific && show('filter-managed-by') && (
        <ManagedByFilter filters={filters} onSetManagedByFilter={onSetManagedByFilter} />
      )}

      {show('filter-school-year') && (
        <SchoolYearFilter filters={filters} onSetSchoolYearFilter={onSetSchoolYearFilter} />
      )}

      {activeTab === 'information-meeting' && show('filter-recurrence-information-meeting') && (
        <RecurrenceFilter filters={filters} onSetRecurrenceFilter={onSetRecurrenceFilter} />
      )}

      {activeTab === 'welcome-meeting' && show('filter-recurrence-welcome-meeting') && (
        <RecurrenceFilter filters={filters} onSetRecurrenceFilter={onSetRecurrenceFilter} />
      )}
    </div>
  );
};
